import React, { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import Archive from "@mui/icons-material/Archive";
import { withStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { withState } from "../../../services/state";
import {
  getEvents as apiGetEvents,
  createEvent as apiCreateEvent,
  deleteEvent as apiDeleteEvent,
  archiveEvent as apiArchiveEvent,
  updateEvent as apiUpdateEvent,
  getTeams as apiGetTeams,
  getEventTypes as apiGetEventTypes,
  getClubUnpaidSummary as apiGetClubUnpaidSummary,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import DialogModal from "../../components/dialogModal/DialogModal";
import YesNoDialogModal from "../../components/yesNoDialogModal/YesNoDialogModal";
import Section from "../../components/section/Section";
import Button from "../../components/button/Button";

import AddEventForm from "../../forms/addEventForm/AddEventForm";
import EditEventForm from "../../forms/editEventForm/EditEventForm";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  eventHeader: {
    backgroundColor: "#c13535",
    color: "#fff",
    padding: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  infoClick: {
    marginLeft: 5,
    cursor: "pointer",
  },
  eventsButtons: {
    padding: 20,
    textAlign: "right",
  },
});

const getPlayerType = (currentClub) => {
  if (currentClub.isTreasurer) return "treasurer";
  if (currentClub.isCaptain) return "captain";
  return "player";
};

const event_info = {
  treasurer: `This is a list of all team events you’re a captain for. From here you can Create new Events/Fixtures,  see which events/fixtures still require payments for, and archive your completed Events/Fixtures where all payments are complete.\n\nThe Total Fees shows the initial combined amount of fees when players were added to the event/fixture. Adjusted Total Fees shows the new amount taking into account any adjustments that have been made. Unpaid Fees shows exactly how much is still outstanding on any event/fixture.`,
  captain: `This is a list of all team events you’re a captain for. From here you can Create new Events/Fixtures,  see which events/fixtures still require payments for, and archive your completed Events/Fixtures where all payments are complete.\n\nThe Total Fees shows the initial combined amount of fees when players were added to the event/fixture. Adjusted Total Fees shows the new amount taking into account any adjustments that have been made. Unpaid Fees shows exactly how much is still outstanding on any event/fixture.`,
};

const event_created = {
  captain:
    "Unconfirmed events are events you haven't yet finalised. Once you confirm these, players will be notified and you can start receiving payments. Please note you won't be able to make changes to an event once confirmed so please ensure that all the details are correct.",
  treasurer:
    "Unconfirmed events are events you haven't yet finalised. Once you confirm these, players will be notified and you can start receiving payments. Please note you won't be able to make changes to an event once confirmed so please ensure that all the details are correct.",
};

const event_unpaid = {
  treasurer:
    "Unpaid events are those which some team members have not yet paid for. Click an event to view a list of who's paid and who hasn't. You may remove events for which no one has yet paid (for example, if you need to change the date.)",
  captain:
    "Unpaid events are those which some team members have not yet paid for. Click an event to view a list of who's paid and who hasn't. You may remove events for which no one has yet paid (for example, if you need to change the date.)",
};

const event_paid = {
  treasurer: "Paid events are those for which all team members have now paid.",
  captain: "Paid events are those for which all team members have now paid.",
};

const no_events = {
  treasurer:
    "There are no events scheduled at the moment. You can create an event by clicking on the CREATE button above.",
  captain:
    "There are no events scheduled at the moment. You can create an event by clicking on the CREATE button above.",
};

function AdminEventsPage({ classes, dispatch, context }) {
  const [showAddEvent, toggleShowAddEvent] = React.useState(false);
  const [newEvent, storeNewEvent] = React.useState(false);
  const [gettingEvents, setGettingEvents] = React.useState(false);
  const [events, setEvents] = React.useState([]);
  const [creatingEvent, setCreatingEvent] = React.useState(true);
  const [createEventError, setCreateEventError] = React.useState(true);
  const [editEvent, setEditEvent] = React.useState(null);
  const [updatingEvent, setUpdatingEvent] = React.useState(true);
  const [updateEventError, setUpdateEventError] = React.useState(true);
  const [showDeleteEvent, setDeleteEvent] = React.useState(null);
  const [deletingEvent, setDeletingEvent] = React.useState(false);
  const [deleteEventError, setDeleteEventError] = React.useState("");
  const [showArchiveEvent, setArchiveEvent] = React.useState(null);
  const [archivingEvent, setArchivingEvent] = React.useState(false);
  const [archiveEventError, setArchiveEventError] = React.useState("");
  // const [gettingTeams, setGettingTeams] = React.useState(false);
  const [teams, setTeams] = React.useState([]);
  // const [gettingEventTypes, setGettingEventTypes] = React.useState(false);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [redirectFlowUrl, setRedirectFlowUrl] = React.useState(null);
  const [showEventsInfo, setShowEventsInfo] = React.useState(null);
  const [showUnconfirmedEventInfo, setShowUnconfirmedEventInfo] =
    React.useState(null);
  const [showUnpaidEventInfo, setShowUnpaidEventInfo] = React.useState(null);
  const [showPaidEventInfo, setShowPaidEventInfo] = React.useState(null);
  const [unpaidSummary, setUnpaidSummary] = React.useState(null);
  const [gettingUnpaidSummary, setGettingUnpaidSummary] = React.useState(false);

  const getClubUnpaidSummary = useCallback(async () => {
    if (!context.currentClub || !context.currentClub.clubId) {
      return false;
    }

    try {
      setGettingUnpaidSummary(true);
      const data = await apiGetClubUnpaidSummary(context.currentClub.clubId);
      if (data && !data.reason) {
        setUnpaidSummary(data);
      }
      setGettingUnpaidSummary(false);
    } catch (e) {
      console.log(e);
      setGettingUnpaidSummary(false);
    }
  }, [context.currentClub]);

  const getEvents = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingEvents(true);
      const data = await apiGetEvents(
        context.currentClub.clubId,
        context.currentPlayer.id,
      );
      if (data && !data.reason) {
        setEvents(data);
      }
      setGettingEvents(false);
    } catch (e) {
      console.log(e);
      setGettingEvents(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const createEvent = async (payload) => {
    try {
      setCreatingEvent(true);
      await apiCreateEvent(
        context.currentClub.clubId,
        context.currentPlayer.id,
        {
          name: payload.name,
          eventDate: payload.eventDate || new Date(),
          teamId: payload.teamId,
          eventTypeId: payload.eventTypeId,
        },
      );
      setCreatingEvent(false);
      toggleShowAddEvent(false);
      getEvents();
    } catch (e) {
      console.log(e);
      setCreatingEvent(false);
      setCreateEventError(e.message);
    }
  };

  const updateEvent = async (payload) => {
    try {
      setUpdatingEvent(true);
      await apiUpdateEvent(
        context.currentClub.clubId,
        payload.id,
        context.currentPlayer.id,
        payload,
      );
      setEditEvent(null);
      setUpdatingEvent(false);
      getEvents();
    } catch (e) {
      setUpdatingEvent(false);
      setUpdateEventError(e.message);
    }
  };

  const deleteEvent = async () => {
    try {
      setDeletingEvent(true);
      await apiDeleteEvent(showDeleteEvent.id);
      setDeletingEvent(false);
      setDeleteEvent(null);
      getEvents();
    } catch (e) {
      setDeletingEvent(false);
      setDeleteEventError(e.message);
    }
  };

  const getTeams = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingTeams(true);
      const data = await apiGetTeams(
        context.currentClub.clubId,
        context.currentPlayer.id,
      );
      if (data && !data.reason) {
        setTeams(data);
      }
      // setGettingTeams(false);
    } catch (e) {
      console.log(e);
      // setGettingTeams(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const getEventTypes = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingEventTypes(true);
      const data = await apiGetEventTypes(
        context.currentClub.clubId,
        context.currentPlayer.id,
      );
      if (data && !data.reason) {
        setEventTypes(data);
      }
      // setGettingEventTypes(false);
    } catch (e) {
      console.log(e);
      // setGettingEventTypes(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const archiveEvent = async () => {
    try {
      setArchivingEvent(true);
      await apiArchiveEvent({ eventId: showArchiveEvent.id, view: "admin" });
      setArchivingEvent(null);
      setArchiveEvent(null);
      getEvents();
    } catch (e) {
      console.log(e);
      setArchivingEvent(false);
      setArchiveEventError(e.message);
    }
  };

  const renderEvents = (filteredEvents, title, backgroundColor, draft) => {
    return (
      <div style={{ marginBottom: "40px" }}>
        <div className={classes.tableWrapper}>
          <div
            style={{
              backgroundColor,
              color: "#fff",
              padding: "10px",
              width: "100%",
            }}
          >
            {title}
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table
              className={classes.table}
              style={{
                minWidth: "1000px",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: "130px" }}>Team</TableCell>
                  <TableCell style={{ minWidth: "130px" }}>Event</TableCell>
                  <TableCell style={{ minWidth: "130px" }}>Date</TableCell>
                  <TableCell style={{ minWidth: "130px" }}>Type</TableCell>
                  <TableCell style={{ minWidth: "130px" }}>
                    Total Fees
                  </TableCell>
                  <TableCell style={{ minWidth: "130px" }}>
                    Adjusted Total Fees
                  </TableCell>
                  <TableCell style={{ minWidth: "130px" }}>
                    Unpaid Fees
                  </TableCell>
                  <TableCell style={{ minWidth: "130px" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.map((event) => {
                  const unreadMessageCount = event.currentPlayerFee?.unreadMessageCount || 0;
                  return (
                    <TableRow key={event.id}>
                      <TableCell>{event.team.name}</TableCell>
                      <TableCell>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {unreadMessageCount > 0 && (
                            <div style={{ position: "relative" }}>
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#2CB045",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                flexShrink: 0,
                              }}>
                                <svg style={{ color: "#fff", fontSize: "16px", width: "16px", height: "16px" }} viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                              </div>
                              <div style={{
                                position: "absolute",
                                top: "-5px",
                                right: "-5px",
                                backgroundColor: "#FF3B30",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "bold",
                                color: "#fff",
                              }}>
                                {unreadMessageCount}
                              </div>
                            </div>
                          )}
                          <Link
                            to={
                              draft
                                ? `/events/${event.id}/edit`
                                : `/events/${event.id}`
                            }
                          >
                            {event.name}
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell>
                        {event.eventDate && (
                          <div className={classes.title}>
                            {formatInTimeZone(
                              event.eventDate,
                              "UTC",
                              "do MMMM yyyy",
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{event.type.name}</TableCell>
                      <TableCell>{`£${event.totalFee.toFixed(2)}`}</TableCell>
                      <TableCell>{`£${event.adjustedFee.toFixed(
                        2,
                      )}`}</TableCell>
                      <TableCell>{`£${event.unpaidFee.toFixed(2)}`}</TableCell>
                      <TableCell>{getActionButton(event)}</TableCell>
                    </TableRow>
                  );
                })}
                {!gettingEvents && filteredEvents.length === 0 && (
                  <TableRow style={{ textAlign: "center", padding: "20px 0" }}>
                    <TableCell colSpan={8} style={{ textAlign: "center" }}>
                      {no_events[getPlayerType(context.currentClub)]}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  const renderDraftEvents = () => {
    const filteredEvents = events.filter((event) => !event.isConfirmed);
    if (events && events.length > 0) {
      return renderEvents(filteredEvents, "Draft Events", "#7d7d7d", true);
    }
  };

  const renderFailedEvents = () => {
    const filteredEvents = events.filter((event) => {
      if (context.currentClub.isTreasurer) {
        if (event.hideForTreasurers) {
          return false;
        }
      } else if (event.isCaptainForEventTeam) {
        if (event.hideForCaptains) {
          return false;
        }
      }

      return (
        event.isConfirmed && !event.hideForCaptains && event.paymentsFailed
      );
    });

    if (events && events.length > 0) {
      return renderEvents(
        filteredEvents,
        "Confirmed Events with Failed Payments",
        "#b82828",
        true,
      );
    }
  };

  const renderOutstandingEvents = () => {
    const filteredEvents = events.filter((event) => {
      if (context.currentClub.isTreasurer) {
        if (event.hideForTreasurers) {
          return false;
        }
      } else if (event.isCaptainForEventTeam) {
        if (event.hideForCaptains) {
          return false;
        }
      }

      return (
        event.isConfirmed &&
        !event.haveAllPlayersSubmitted &&
        !event.haveAllPlayersPaid &&
        (context.currentClub.isTreasurer || event.isCaptainForEventTeam)
      );
    });

    if (events && events.length > 0) {
      return renderEvents(
        filteredEvents,
        "Confirmed Events with Outstanding Payments",
        "#c37b15",
        false,
      );
    }
  };

  const renderPaymentSubmittedEvents = () => {
    const filteredEvents = events.filter((event) => {
      if (context.currentClub.isTreasurer) {
        if (event.hideForTreasurers) {
          return false;
        }
      } else if (event.isCaptainForEventTeam) {
        if (event.hideForCaptains) {
          return false;
        }
      }

      return (
        event.isConfirmed &&
        event.haveAllPlayersSubmitted &&
        !event.haveAllPlayersPaid &&
        (context.currentClub.isTreasurer || event.isCaptainForEventTeam)
      );
    });
    if (events && events.length > 0) {
      return renderEvents(
        filteredEvents,
        "All Players Submitted Payments",
        "#07843e",
        false,
      );
    }
  };

  const renderPaidEvents = () => {
    const filteredEvents = events.filter((event) => {
      if (context.currentClub.isTreasurer) {
        if (event.hideForTreasurers) {
          return false;
        }
      } else if (event.isCaptainForEventTeam) {
        if (event.hideForCaptains) {
          return false;
        }
      }

      return (
        event.isConfirmed &&
        event.haveAllPlayersPaid &&
        (context.currentClub.isTreasurer || event.isCaptainForEventTeam)
      );
    });
    if (events && events.length > 0) {
      return renderEvents(filteredEvents, "All Players Paid", "#07843e", false);
    }
  };

  const getActionButton = (event) => {
    if (!event.isConfirmed) {
      return <Button onClick={() => setDeleteEvent(event)} title="Remove" />;
    }

    if (event.haveAllPlayersPaid) {
      return <Button onClick={() => setArchiveEvent(event)} title="Archive" />;
    }

    if (!event.haveAnyPlayersPaid) {
      return <Button onClick={() => setDeleteEvent(event)} title="Remove" />;
    }
  };

  useEffect(() => {
    getClubUnpaidSummary();
    getEvents();
    getTeams();
    getEventTypes();
  }, [getEvents, getTeams, getEventTypes, getClubUnpaidSummary]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title={`${context.currentClub.isTreasurer ? "Admin" : "Captain"} Events`}
      onInfoClick={
        event_info[getPlayerType(context.currentClub)]
          ? () => setShowEventsInfo(true)
          : null
      }
    >
      <Section style={{ padding: 0, backgroundColor: "transparent" }}>
        {!gettingUnpaidSummary && unpaidSummary && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "1rem",
              padding: "1rem",
              backgroundColor: "#fff",
              borderBottom: "1px solid #ddd",
            }}
          >
            <div
              style={{
                flex: "1 1 150px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: "bold" }}>Unpaid Events</div>
              <div style={{ fontSize: 25 }}>{unpaidSummary.unpaidEvents}</div>
            </div>
            <div
              style={{
                flex: "1 1 150px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: "bold" }}>Unpaid Fees</div>
              <div style={{ fontSize: 25 }}>
                £{unpaidSummary.unpaidFees.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 100px",
                textAlign: "center",
              }}
            >
              <div style={{ maxWidth: "100px", margin: "0 auto" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    borderBottom: "4px solid #47d45a",
                    paddingBottom: "10px",
                    marginBottom: "15px",
                  }}
                >
                  0 - 7 days
                </div>
              </div>
              <div style={{ fontSize: 25 }}>
                £{unpaidSummary.last7Days.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 100px",
                textAlign: "center",
              }}
            >
              <div style={{ maxWidth: "100px", margin: "0 auto" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    borderBottom: "4px solid #47d45a",
                    paddingBottom: "10px",
                    marginBottom: "15px",
                  }}
                >
                  8 - 14 days
                </div>
              </div>
              <div style={{ fontSize: 25 }}>
                £{unpaidSummary.last14Days.toFixed(2)}
              </div>
            </div>
            <div
              style={{
                flex: "1 1 100px",
                textAlign: "center",
              }}
            >
              <div style={{ maxWidth: "100px", margin: "0 auto" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    borderBottom: "4px solid #47d45a",
                    paddingBottom: "10px",
                    marginBottom: "15px",
                  }}
                >
                  14+ days
                </div>
              </div>
              <div style={{ fontSize: 25 }}>
                £{unpaidSummary.over14Days.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        <div className={classes.eventsButtons}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Link to="/archive/admin">
              <div
                style={{
                  marginRight: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecorationLine: "none",
                  color: "#000",
                }}
              >
                Archive
                <Archive
                  style={{ color: "#000", fontSize: "30px", marginLeft: "5px" }}
                />
              </div>
            </Link>
            {(context.currentClub.isTreasurer ||
              context.currentClub.isCaptain) && (
              <Link to="/events/new">
                <Button title="Create" />
              </Link>
            )}
          </div>
        </div>
        {renderDraftEvents()}
        {renderFailedEvents()}
        {renderOutstandingEvents()}
        {renderPaymentSubmittedEvents()}
        {renderPaidEvents()}
      </Section>

      {showAddEvent && (
        <DialogModal
          submitText="Add"
          onSubmit={() => createEvent(newEvent)}
          onClose={() => toggleShowAddEvent(false)}
          error
        >
          <AddEventForm
            payload={newEvent}
            storePayload={(payload) => storeNewEvent(payload)}
            errorMessage={createEventError}
            submitting={creatingEvent}
            teams={teams}
            eventTypes={eventTypes}
          />
        </DialogModal>
      )}

      {editEvent && (
        <DialogModal
          submitText="Update"
          onSubmit={() => updateEvent(editEvent)}
          onClose={() => setEditEvent(null)}
          error
        >
          <EditEventForm
            payload={editEvent}
            storePayload={(payload) => setEditEvent(payload)}
            errorMessage={updateEventError}
            submitting={updatingEvent}
            teams={teams}
            eventTypes={eventTypes}
          />
        </DialogModal>
      )}

      {showDeleteEvent && (
        <YesNoDialogModal
          title="Are you sure you want to delete event?"
          onClose={() => setDeleteEvent(false)}
          onSubmit={() => deleteEvent()}
          submitting={deletingEvent}
          error={deleteEventError}
        />
      )}

      {showArchiveEvent && (
        <YesNoDialogModal
          title="Are you sure you want to archive event?"
          onClose={() => setArchiveEvent(false)}
          onSubmit={() => archiveEvent()}
          submitting={archivingEvent}
          error={archiveEventError}
        />
      )}

      {redirectFlowUrl && (
        <DialogModal
          title="Setup your direct debit"
          onClose={() => setRedirectFlowUrl(null)}
        >
          <div>
            You haven't yet set up direct debits for Slate. Click{" "}
            <a href={redirectFlowUrl}>here</a> to do this now.
          </div>
        </DialogModal>
      )}

      {showEventsInfo && (
        <DialogModal
          title={`${
            getPlayerType(context.currentClub) === "treasurer"
              ? "Admin"
              : "Captain"
          } events`}
          onClose={() => setShowEventsInfo(null)}
          cancelText="Close"
        >
          <div style={{ whiteSpace: "break-spaces" }}>
            {event_info[getPlayerType(context.currentClub)]}
          </div>
        </DialogModal>
      )}

      {showUnconfirmedEventInfo && (
        <DialogModal
          title="Unconfirmed events"
          onClose={() => setShowUnconfirmedEventInfo(null)}
          cancelText="Close"
        >
          <div>{event_created[getPlayerType(context.currentClub)]}</div>
        </DialogModal>
      )}

      {showUnpaidEventInfo && (
        <DialogModal
          title="Unpaid events"
          onClose={() => setShowUnpaidEventInfo(null)}
          cancelText="Close"
        >
          <div>{event_unpaid[getPlayerType(context.currentClub)]}</div>
        </DialogModal>
      )}

      {showPaidEventInfo && (
        <DialogModal
          title="Unconfirmed events"
          onClose={() => setShowPaidEventInfo(null)}
          cancelText="Close"
        >
          <div>{event_paid[getPlayerType(context.currentClub)]}</div>
        </DialogModal>
      )}
    </PageWrapper>
  );
}

export default withStyles(styles)(withState(AdminEventsPage));
